import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { useDialog } from "@/context/DialogProvider";
import api, { formatErr } from "@/lib/api";
import { Portal } from "@/components/Portal";
import { toast } from "sonner";
import {
  Thermometer, Flame, Snowflake, Clock, Plus, X, ChevronRight, ShieldCheck,
  AlertTriangle, CheckCircle2, Utensils, Trash2, RefreshCw, ChefHat, MapPin,
  Droplets, Radar,
} from "lucide-react";
import ThawingTab from "@/pages/ThawingTab";
import MetalDetectorTab from "@/pages/MetalDetectorTab";
import TemperatureOcrField from "@/components/TemperatureOcrField";
import PrepListUploadModal from "@/pages/PrepListUploadModal";
import HoldPrepListUploadModal from "@/pages/HoldPrepListUploadModal";
import HoldPrepListFillModal from "@/pages/HoldPrepListFillModal";
import PrepListFillModal from "@/pages/PrepListFillModal";

const VERIFY_ROLES = ["owner", "manager", "supervisor"];
const COOK_DISPOSITIONS = { served: "Serve immediately", cool_store: "Cool & store (cook-chill)" };
const COOL_METHODS = {
  fridge:       "Fridge (standard refrigeration)",
  blast_chill:  "Blast chill",
  blast_freeze: "Blast freeze",
};
const STATUS_LABEL = { open: "In progress", cold_storage: "Cold Storage", awaiting_verification: "Awaiting verification", verified: "Verified" };

// Format a YYYY-MM-DD ISO date into the tenant's preferred layout.
// Supports DD/MM/YYYY and MM/DD/YYYY; falls back to the raw ISO string.
function formatTenantDate(iso, pattern) {
  if (!iso || iso.length < 10) return iso || "";
  const [y, m, d] = iso.slice(0, 10).split("-");
  if (!y || !m || !d) return iso;
  if ((pattern || "").toUpperCase() === "MM/DD/YYYY") return `${m}/${d}/${y}`;
  return `${d}/${m}/${y}`;   // default DD/MM/YYYY
}

// Legacy cooling docs (created before the two-step flow) have no `status`
// field — treat them as complete so historical records still render right.
const isCoolingComplete = (c) => !!c && (c.status || "complete") === "complete";
const isCoolingInProgress = (c) => !!c && c.status === "in_progress";

/** Derive a display status from a batch. A batch whose cooking path is
 *  "cool & store" and has cooling logged but no reheat yet is effectively
 *  sitting in cold storage — we surface that as its own pill so kitchen
 *  managers can filter for what's currently waiting to be reheated. */
function derivedStatus(b) {
  if (b?.status === "open" && b?.disposition === "cool_store" && isCoolingComplete(b?.cooling) && !b?.reheat) {
    return "cold_storage";
  }
  return b?.status;
}

const dtLocalNow = () => {
  const d = new Date(); d.setSeconds(0, 0);
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};
const toISO = (v) => (v ? new Date(v).toISOString() : null);
// Convert an ISO string (possibly UTC or with an offset) into the
// `datetime-local` input value in the BROWSER's local timezone, so an
// input pre-populated with a saved timestamp shows the same wall-clock
// the user originally entered. Slicing the raw ISO breaks TZ (a UTC
// start renders as if it were local, which then makes any later "end"
// converted through toISO() appear to precede the start).
const isoToDatetimeLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—");

function useSiteUsers() {
  const [users, setUsers] = useState([]);
  useEffect(() => {
    api.get("/hot-cold-hold/site-users").then(({ data }) => setUsers(data.users || [])).catch(() => setUsers([]));
  }, []);
  return users;
}

// Fetches food-production facilities the caller can pick when logging a
// cooking / hold record. Scoped server-side to the active site so we
// never surface a kitchen from a different property. Refreshes when the
// active site changes.
function useKitchenFacilities() {
  const { activeSiteId } = useSite();
  const [facilities, setFacilities] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api.get("/hot-cold-hold/facilities")
      .then(({ data }) => { if (!cancelled) setFacilities(data.facilities || []); })
      .catch(() => { if (!cancelled) setFacilities([]); });
    return () => { cancelled = true; };
  }, [activeSiteId]);
  return facilities;
}

function FacilitySelect({ facilities, value, onChange, testid }) {
  return (
    <select
      className="vb-input" required value={value || ""} data-testid={testid}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="" disabled>— Pick a kitchen —</option>
      {facilities.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
    </select>
  );
}

function UserSelect({ users, me, valueId, onPick, testid }) {
  const list = users.length || !me ? users : [{ id: me.id, name: me.name || me.email }];
  return (
    <select
      className="vb-input" value={valueId || ""} data-testid={testid}
      onChange={(e) => { const u = list.find((x) => x.id === e.target.value); onPick(u ? { id: u.id, name: u.name } : { id: "", name: "" }); }}
    >
      {list.length === 0 && <option value="">—</option>}
      {list.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
    </select>
  );
}

export default function DailyKitchenRecord() {
  const { user, isFeatureEnabled } = useAuth();
  const { confirm } = useDialog();
  const { isAllSites, canPickAllSites, activeSite } = useSite();
  const facilities = useKitchenFacilities();
  const canVerify = VERIFY_ROLES.includes(user?.role);
  const needsSite = canPickAllSites && isAllSites;
  // Sites with no `food_production` facility can't host a record at all —
  // the required Kitchen dropdown would be empty. Block the "New" buttons
  // so operators don't open a modal they can't submit.
  const noFacility = !needsSite && facilities.length === 0;
  const [tab, setTab] = useState("cooking");
  const [config, setConfig] = useState(null);

  useEffect(() => {
    api.get("/hot-cold-hold/config").then(({ data }) => setConfig(data.config || {})).catch(() => setConfig({}));
  }, []);

  return (
    <div className="vb-fade-in" data-testid="daily-kitchen-record-page">
      <div className="flex items-center gap-3 mb-1">
        <Thermometer size={26} style={{ color: "var(--vb-accent)" }} />
        <h1 className="vb-heading text-3xl" style={{ color: "var(--vb-text-2)" }}>Daily Kitchen Record</h1>
      </div>
      <p className="opacity-70 max-w-2xl mb-4">
        Three kitchen food-safety logs: a <strong>Cooking Record</strong> (cook, cool &amp; store, reheat),
        a standalone <strong>Hot &amp; Cold Hold</strong> log, and a <strong>Thawing</strong> log
        (running-water method, 2-hour safe limit).
      </p>

      {/* Site-context header — echoes what the master top-bar picker
          resolved to, so it's immediately visible on this page which
          site's records are being shown (no per-row column needed). */}
      <div
        className="vb-card p-3 mb-4 flex items-center gap-2"
        data-testid="dkr-site-context"
        style={{ background: "var(--vb-bg-alt, var(--vb-bg))", borderColor: "var(--vb-border)" }}
      >
        <MapPin size={16} style={{ color: "var(--vb-accent)" }} />
        <span className="text-sm">
          Showing records for{" "}
          {needsSite
            ? <strong>all sites</strong>
            : <strong data-testid="dkr-site-name">{activeSite?.name || "—"}</strong>}
          {activeSite?.location && !needsSite && (
            <span className="opacity-60"> · {activeSite.location}</span>
          )}
          <span className="opacity-60">
            . Switch site from the top-bar to see other sites&rsquo; records.
          </span>
        </span>
      </div>

      {needsSite && (
        <div className="vb-pill warn mb-4" data-testid="dkr-pick-site-hint">
          Select a specific site (top-left) before logging a record.
        </div>
      )}
      {noFacility && (
        <div className="vb-pill warn mb-4" data-testid="dkr-no-facility-hint">
          <strong>{activeSite?.name || "This site"}</strong> has no food-production
          kitchen yet. Ask your owner to add one under Sites → Facilities before
          logging a record.
        </div>
      )}

      <div
        className="flex gap-2 mb-5 border-b overflow-x-auto vb-hscroll flex-nowrap"
        style={{ borderColor: "var(--vb-border)" }}
        data-testid="dkr-tabs"
      >
        {/* Tab visibility is gated by per-tenant feature flags. The 4
            sub-features (Cooking Record / Hot & Cold Hold / Thawing /
            Metal Detector) each toggle independently. If a tab's flag
            is off, we hide the button AND redirect if it was active. */}
        {[
          ["cooking", "Cooking Record", ChefHat, "cooking-record"],
          ["holding", "Hot & Cold Hold", Thermometer, "hot-cold-hold"],
          ["thawing", "Thawing", Droplets, "thawing"],
          ["metal_detector", "Metal Detector", Radar, "metal-detector"],
        ].filter(([, , , fkey]) => isFeatureEnabled(fkey)).map(([k, label, Icon]) => (
          <button
            key={k} onClick={() => setTab(k)} data-testid={`dkr-tab-${k}`}
            className="dkr-tab-btn px-3 py-2 text-sm font-semibold inline-flex items-center gap-1.5 -mb-px border-b-2 transition-colors flex-shrink-0"
            style={{ borderColor: tab === k ? "var(--vb-accent)" : "transparent", color: tab === k ? "var(--vb-text-2)" : "var(--vb-text-1)", opacity: tab === k ? 1 : 0.6 }}
          >
            <Icon size={15} className="flex-shrink-0" />
            <span className="dkr-tab-label">{label}</span>
          </button>
        ))}
      </div>

      {tab === "cooking" && isFeatureEnabled("cooking-record")
        ? <CookingTab config={config} canVerify={canVerify} needsSite={needsSite} noFacility={noFacility} confirm={confirm} />
        : tab === "holding" && isFeatureEnabled("hot-cold-hold")
        ? <HoldingTab config={config} canVerify={canVerify} needsSite={needsSite} noFacility={noFacility} confirm={confirm} />
        : tab === "thawing" && isFeatureEnabled("thawing")
        ? <ThawingTab canVerify={canVerify} needsSite={needsSite} noFacility={noFacility} />
        : tab === "metal_detector" && isFeatureEnabled("metal-detector")
        ? <MetalDetectorTab canVerify={canVerify} needsSite={needsSite} noFacility={noFacility} />
        : <div className="opacity-70 text-sm py-8 text-center" data-testid="dkr-no-tabs-enabled">
            No Daily Kitchen Record features are enabled for this workspace.
          </div>}
    </div>
  );
}

function StatusPill({ status }) {
  const cls = status === "verified" ? "ok"
    : status === "awaiting_verification" ? "warn"
    : status === "cold_storage" ? "info"
    : "";
  return <span className={`vb-pill ${cls} text-[10px]`}
    style={{ whiteSpace: "nowrap" }}
    data-testid={`dkr-status-${status}`}>{STATUS_LABEL[status] || status}</span>;
}
function FlagPill({ breach }) {
  return breach
    ? <span className="vb-pill err text-[10px]"><AlertTriangle size={11} /> NC</span>
    : <span className="vb-pill ok text-[10px]"><CheckCircle2 size={11} /> OK</span>;
}
function Field({ label, children, hint }) {
  return (
    <div>
      <label className="text-xs uppercase font-semibold opacity-70">{label}</label>
      {children}
      {hint && <p className="text-[11px] opacity-60 mt-0.5">{hint}</p>}
    </div>
  );
}
function CorrectiveBox({ show, value, onChange, reasons = [], testid = "dkr-corrective-input" }) {
  if (!show) return null;
  return (
    <div className="p-3 rounded-lg" style={{ background: "rgba(220,38,38,0.07)", border: "1px solid rgba(220,38,38,0.3)" }} data-testid="dkr-corrective-box">
      <div className="flex items-center gap-2 text-sm font-semibold mb-1" style={{ color: "#b91c1c" }}>
        <AlertTriangle size={14} /> Out of range — corrective action required
      </div>
      {reasons.length > 0 && (
        <ul className="text-[12px] mb-2 pl-4 list-disc" style={{ color: "#991b1b" }} data-testid="dkr-corrective-reasons">
          {reasons.map((r, i) => <li key={i}>{r}</li>)}
        </ul>
      )}
      <textarea className="vb-input" rows={2} value={value} onChange={onChange}
        placeholder="e.g. Continued cooking to 78°C / discarded / re-chilled" data-testid={testid} />
    </div>
  );
}
function Filters({ value, onChange, prefix, children }) {
  // Cold-storage is a synthetic status only meaningful for cooking-record
  // batches (a cool_store batch after cooling, before reheat). Hide the
  // pill on the Hot & Cold Hold tab where "open" itself is also hidden.
  const options = ["all", "open", "cold_storage", "awaiting_verification", "verified"].filter((s) => {
    if (prefix === "hold" && (s === "open" || s === "cold_storage")) return false;
    return true;
  });
  return (
    <div className="flex gap-2 mb-4 items-center overflow-x-auto vb-hscroll flex-nowrap">
      {options.map((s) => (
        <button key={s} className={`vb-pill flex-shrink-0 whitespace-nowrap ${value === s ? "ok" : ""}`} style={{ cursor: "pointer" }}
          onClick={() => onChange(s)} data-testid={`${prefix}-filter-${s}`}>
          {s === "all" ? "All" : STATUS_LABEL[s]}
        </button>
      ))}
      {children}
    </div>
  );
}

/* ===================== COOKING TAB ===================== */
function CookingTab({ config, canVerify, needsSite, noFacility, confirm }) {
  const { activeSiteId } = useSite();
  const { tenant } = useAuth();
  const tenantDateFormat = (tenant?.operational?.date_format || "DD/MM/YYYY").toUpperCase();
  const facilities = useKitchenFacilities();
  const [batches, setBatches] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showCook, setShowCook] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [selected, setSelected] = useState(null);
  const [prepLists, setPrepLists] = useState([]);
  const [openPrepList, setOpenPrepList] = useState(null);
  const [query, setQuery] = useState("");
  // Backlog window keeps the summary from growing unbounded. Default
  // 30 days matches most kitchens' inspection cadence; verified rows
  // beyond that stay in the Audit Vault, not on-screen.
  const [backlogDays, setBacklogDays] = useState("30");
  const [verifiedOpen, setVerifiedOpen] = useState(false);

  const load = useCallback(async () => {
    // Cold-storage is derived client-side (cool_store + cooling + !reheat)
    // so we always fetch a wider server-side bucket and refine locally.
    const serverStatus = statusFilter === "cold_storage" ? "open" : statusFilter;
    const { data } = await api.get(`/hot-cold-hold/batches?status=${serverStatus}`);
    let rows = data.batches || [];
    if (statusFilter === "cold_storage") {
      rows = rows.filter((b) => derivedStatus(b) === "cold_storage");
    } else if (statusFilter === "open") {
      // "In progress" should NOT include cold-storage rows — they get
      // their own bucket so the two states aren't visually conflated.
      rows = rows.filter((b) => derivedStatus(b) !== "cold_storage");
    }
    setBatches(rows);
  }, [statusFilter]);

  const loadPrepLists = useCallback(async () => {
    // Active prep lists = pending or in_progress. Completed/closed lists
    // stay off the tile shelf so it doesn't clutter service.
    try {
      const { data } = await api.get("/hot-cold-hold/prep-lists?status=pending");
      const { data: inProg } = await api.get("/hot-cold-hold/prep-lists?status=in_progress");
      const merged = [...(data.prep_lists || []), ...(inProg.prep_lists || [])];
      // De-dupe & sort by cook_date asc.
      const map = new Map();
      merged.forEach((p) => map.set(p.id, p));
      const list = [...map.values()].sort((a, b) => a.cook_date.localeCompare(b.cook_date));
      setPrepLists(list);
    } catch { /* silent — the panel just stays empty */ }
  }, []);
  // Re-fetch whenever the master site filter (top bar) changes — the
  // X-Site-Context header set by our axios interceptor lives on the
  // NEXT request, so we need to explicitly trigger a reload on switch.
  useEffect(() => { load(); loadPrepLists(); }, [load, loadPrepLists, activeSiteId]);

  const openDetail = async (id) => { const { data } = await api.get(`/hot-cold-hold/batches/${id}`); setSelected(data.batch); };
  const openPrepFill = async (id) => {
    const { data } = await api.get(`/hot-cold-hold/prep-lists/${id}`);
    setOpenPrepList(data.prep_list);
  };
  const deletePrepList = async (p, hasFills) => {
    if (hasFills) {
      toast.error("Some dishes are already filled — open the list and use 'Close' instead.");
      return;
    }
    const ok = await confirm({
      title: "Delete this prep list?",
      message: `This will permanently remove "${p.facility_name} · ${p.cook_date}" (${p.progress?.total || 0} dishes). This can't be undone.`,
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/hot-cold-hold/prep-lists/${p.id}`);
      toast.success("Prep list deleted.");
      await loadPrepLists();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Couldn't delete prep list.");
    }
  };

  return (
    <div data-testid="cooking-tab">
      <div className="flex justify-end gap-2 mb-3">
        <button className="vb-btn ghost" onClick={() => setShowUpload(true)} disabled={needsSite || noFacility}
          data-testid="upload-prep-list" title={noFacility ? "Add a food-production facility first." : undefined}>
          <Utensils size={16} /> Upload prep list
        </button>
        <button className="vb-btn" onClick={() => setShowCook(true)} disabled={needsSite || noFacility}
          data-testid="new-cook-batch" title={noFacility ? "Add a food-production facility to this site before logging a record." : undefined}>
          <Plus size={16} /> New cooking record
        </button>
      </div>

      {prepLists.length > 0 && (
        <div className="mb-3" data-testid="prep-lists-panel">
          <div className="text-[11px] uppercase tracking-wider opacity-70 mb-2 font-semibold">Today's Prep</div>
          {(() => {
            // Group by cook_date so today's dishes never sit beside a list
            // for two days away. Dates are formatted using the tenant's
            // Settings → Operational → Date format preference (defaults DD/MM/YYYY).
            const groups = {};
            prepLists.forEach((p) => {
              const k = p.cook_date;
              (groups[k] = groups[k] || []).push(p);
            });
            const orderedDates = Object.keys(groups).sort();
            const today = new Date().toISOString().slice(0, 10);
            const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
            return orderedDates.map((d) => {
              const label = d === today ? "Today" : d === tomorrow ? "Tomorrow" : "";
              return (
                <div key={d} className="mb-3" data-testid={`prep-list-group-${d}`}>
                  <div className="text-[12px] font-semibold mb-1 flex items-center gap-2">
                    <span>{formatTenantDate(d, tenantDateFormat)}</span>
                    {label && <span className="text-[10px] uppercase tracking-wider opacity-60">· {label}</span>}
                  </div>
                  <div className="flex flex-col gap-2">
                    {groups[d].map((p) => {
                      const pct = p.progress?.total ? Math.round(100 * (p.progress.filled + p.progress.skipped) / p.progress.total) : 0;
                      const hasFills = (p.progress?.filled || 0) > 0;
                      const derived = p.derived_status || "pending";
                      // Map backend list-level statuses onto the same pill
                      // vocabulary the batches table uses so the operator
                      // sees ONE consistent progression everywhere.
                      const pillLabel = {
                        pending: "Pending",
                        in_progress: "In progress",
                        cold_storage: "Cold Storage",
                        awaiting_verification: "Awaiting verification",
                        verified: "Verified",
                      }[derived] || derived;
                      const pillCls = { verified: "ok", awaiting_verification: "warn", cold_storage: "info" }[derived] || "";
                      return (
                        <div key={p.id} role="button" tabIndex={0}
                          className="vb-card p-3 text-left w-full"
                          style={{ cursor: "pointer" }}
                          onClick={() => openPrepFill(p.id)}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openPrepFill(p.id); } }}
                          data-testid={`prep-list-card-${p.id}`}>
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="text-[13px] font-semibold truncate flex items-center gap-2">
                                {p.facility_name}
                                <span className={`vb-pill ${pillCls} text-[10px]`} data-testid={`prep-list-status-${p.id}`}>{pillLabel}</span>
                              </div>
                              <div className="text-[11px] opacity-70 truncate">
                                {COOK_DISPOSITIONS[p.disposition]} · {(p.progress?.filled || 0)}/{p.progress?.total || 0} filled
                              </div>
                            </div>
                            <button type="button"
                              className="vb-btn ghost sm"
                              onClick={(e) => { e.stopPropagation(); deletePrepList(p, hasFills); }}
                              title={hasFills ? "Some dishes are already filled — close the list from inside instead." : "Delete this prep list"}
                              data-testid={`prep-list-delete-${p.id}`}
                              style={{ opacity: hasFills ? 0.4 : 1 }}>
                              <Trash2 size={14} />
                            </button>
                            <ChevronRight size={16} className="opacity-40 shrink-0" />
                          </div>
                          <div className="mt-2" style={{ background: "var(--vb-border)", borderRadius: 999, height: 6, overflow: "hidden" }}>
                            <div style={{ background: "var(--vb-success,#166534)", height: "100%", width: `${pct}%`, transition: "width .2s" }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}

      <Filters value={statusFilter} onChange={setStatusFilter} prefix="cook">
        <div className="ml-auto flex items-center gap-2 text-[12px]">
          <input type="search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search food or order #" className="vb-input sm"
            style={{ width: 180, padding: "4px 8px", fontSize: 12 }}
            data-testid="cook-search" />
          <label className="text-[10px] uppercase tracking-wider opacity-60 whitespace-nowrap">Backlog</label>
          <select className="vb-input sm" style={{ width: 130, padding: "4px 8px", fontSize: 12 }} value={backlogDays}
            onChange={(e) => setBacklogDays(e.target.value)} data-testid="cook-backlog">
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="all">All time</option>
          </select>
        </div>
      </Filters>
      {(() => {
        // Client-side backlog + search filter. Kept out of the render
        // JSX to keep the table markup readable.
        const cutoff = backlogDays === "all"
          ? 0
          : Date.now() - Number(backlogDays) * 86400000;
        const q = query.trim().toLowerCase();
        const inWindow = batches.filter((b) => {
          if (cutoff && new Date(b.created_at || b.cook?.cooked_at || 0).getTime() < cutoff) return false;
          if (!q) return true;
          return (b.food_name || "").toLowerCase().includes(q) ||
                 (b.prep_order_no || "").toLowerCase().includes(q);
        });
        const activeRows = inWindow.filter((b) => derivedStatus(b) !== "verified");
        const verifiedRows = inWindow.filter((b) => derivedStatus(b) === "verified");
        return (
          <div className="vb-card overflow-x-auto">
            <table className="vb-table">
              <thead><tr><th style={{ width: 90 }}>Order #</th><th>Food</th><th>Path</th><th>Cooked</th><th>Status</th><th>Flag</th><th></th></tr></thead>
              <tbody>
                {activeRows.length === 0 && verifiedRows.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign: "center", padding: 28 }} className="opacity-60">No cooking records in this window.</td></tr>
                )}
                {activeRows.map((b) => (
                  <tr key={b.id} data-testid={`cook-row-${b.id}`} style={{ cursor: "pointer" }} onClick={() => openDetail(b.id)}>
                    <td className="opacity-70">{b.prep_order_no || "—"}</td>
                    <td><strong>{b.food_name}</strong></td>
                    <td className="opacity-80">{COOK_DISPOSITIONS[b.disposition]}</td>
                    <td className="opacity-70">{fmt(b.cook?.cooked_at)}</td>
                    <td><StatusPill status={derivedStatus(b)} /></td>
                    <td><FlagPill breach={b.has_breach} /></td>
                    <td style={{ textAlign: "right" }}><ChevronRight size={16} /></td>
                  </tr>
                ))}
                {verifiedRows.length > 0 && (
                  <tr onClick={() => setVerifiedOpen((v) => !v)} style={{ cursor: "pointer", background: "var(--vb-bg-2,#faf6f1)" }}
                    data-testid="cook-verified-header">
                    <td colSpan={7} className="text-[12px] font-semibold py-2 px-3">
                      {verifiedOpen ? "▾" : "▸"} Verified · {verifiedRows.length}
                    </td>
                  </tr>
                )}
                {verifiedOpen && verifiedRows.map((b) => (
                  <tr key={b.id} data-testid={`cook-row-${b.id}`} style={{ cursor: "pointer" }} onClick={() => openDetail(b.id)}>
                    <td className="opacity-70">{b.prep_order_no || "—"}</td>
                    <td><strong>{b.food_name}</strong></td>
                    <td className="opacity-80">{COOK_DISPOSITIONS[b.disposition]}</td>
                    <td className="opacity-70">{fmt(b.cook?.cooked_at)}</td>
                    <td><StatusPill status={derivedStatus(b)} /></td>
                    <td><FlagPill breach={b.has_breach} /></td>
                    <td style={{ textAlign: "right" }}><ChevronRight size={16} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })()}
      {showCook && <CookModal config={config} onClose={() => setShowCook(false)} onSaved={async (b) => { setShowCook(false); await load(); if (b?.disposition !== "cool_store") openDetail(b.id); }} />}
      {showUpload && (
        <PrepListUploadModal
          facilities={facilities}
          onClose={() => setShowUpload(false)}
          onCreated={async (p) => { setShowUpload(false); await loadPrepLists(); openPrepFill(p.id); }} />
      )}
      {openPrepList && (
        <PrepListFillModal prepList={openPrepList} config={config} canVerify={canVerify}
          onClose={() => { setOpenPrepList(null); load(); loadPrepLists(); }}
          onChanged={(p) => setOpenPrepList(p)} />
      )}
      {selected && <BatchDetail batch={selected} config={config} canVerify={canVerify} confirm={confirm}
        onClose={() => setSelected(null)} onChanged={async (id) => { await load(); if (id) openDetail(id); else setSelected(null); }} />}
    </div>
  );
}

function CookModal({ config, onClose, onSaved }) {
  const { user: me } = useAuth();
  const users = useSiteUsers();
  const facilities = useKitchenFacilities();
  // Shared header (one kitchen, one cooked-at time, one operator per
  // session — decided with the user 2026-08-27). Every dish in `items`
  // is otherwise independent.
  const [facilityId, setFacilityId] = useState("");
  const [cookedAt, setCookedAt] = useState(dtLocalNow());
  const [checkedBy, setCheckedBy] = useState({ id: me?.id || "", name: me?.name || "" });
  const cookMin = config?.cook_min_c ?? 75;

  const blankItem = () => ({
    _key: Math.random().toString(36).slice(2),
    food_name: "",
    temp_c: "",
    disposition: "served",
    cool_method: "fridge",
    corrective_action: "",
    notes: "",
    ocr: null,   // { photo_path, parsed_value, confidence }
  });
  const [items, setItems] = useState([blankItem()]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Default to the only kitchen if there's just one.
  useEffect(() => {
    if (!facilityId && facilities.length === 1) setFacilityId(facilities[0].id);
  }, [facilities, facilityId]);

  const setItem = (i, patch) =>
    setItems((xs) => xs.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  // "+ Add another item" copies the last row's disposition + cool_method
  // — 8 out of 10 times consecutive dishes go to the same fate, so this
  // saves taps without hiding the toggle.
  const addItem = () => setItems((xs) => {
    const last = xs[xs.length - 1] || {};
    return [...xs, {
      ...blankItem(),
      disposition: last.disposition || "served",
      cool_method: last.cool_method || "fridge",
    }];
  });
  const removeItem = (i) =>
    setItems((xs) => (xs.length > 1 ? xs.filter((_, idx) => idx !== i) : xs));

  const servedCount = items.filter((x) => x.disposition === "served").length;
  const coolCount = items.filter((x) => x.disposition === "cool_store").length;

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      // Trim + validate every row up-front so a bad row is caught by
      // name rather than by a cryptic server 400.
      const payloadItems = [];
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        const food = it.food_name.trim();
        if (!food) throw new Error(`Row ${i + 1}: food name is required.`);
        if (it.temp_c === "") throw new Error(`Row ${i + 1}: enter the core temperature.`);
        const temp = Number(it.temp_c);
        if (Number.isNaN(temp)) throw new Error(`Row ${i + 1}: temperature must be a number.`);
        const rowBreach = temp < cookMin;
        if (rowBreach && !it.corrective_action.trim()) {
          throw new Error(`Row ${i + 1} (${food}): under ${cookMin}°C — a corrective action is required.`);
        }
        const ocrPayload = it.ocr ? {
          ocr_photo_path: it.ocr.photo_path,
          ocr_parsed_value: it.ocr.parsed_value,
          ocr_confidence: it.ocr.confidence,
          ocr_edited: Math.abs(temp - Number(it.ocr.parsed_value)) > 0.05,
        } : {};
        payloadItems.push({
          food_name: food,
          temp_c: temp,
          disposition: it.disposition,
          cool_method: it.disposition === "cool_store" ? it.cool_method : undefined,
          corrective_action: it.corrective_action,
          notes: it.notes,
          ...ocrPayload,
        });
      }
      const { data } = await api.post("/hot-cold-hold/batches/bulk", {
        facility_id: facilityId,
        cooked_at: toISO(cookedAt),
        checked_by: checkedBy.name,
        checked_by_id: checkedBy.id,
        items: payloadItems,
      });
      const count = data.batches?.length || payloadItems.length;
      const cs = data.batches?.filter((b) => b.disposition === "cool_store").length ?? coolCount;
      const sv = count - cs;
      toast.success(
        `${count} record${count > 1 ? "s" : ""} logged` +
        (cs > 0 ? ` — ${cs} need${cs === 1 ? "s" : ""} cool-and-store follow-up. Tap a row to continue.` :
                  ` (${sv} served)`)
      );
      onSaved(data.batches?.[0]);
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || e2.message || "Failed to save");
    } finally { setBusy(false); }
  };

  return (
    <Portal><div className="vb-modal-bg" onClick={onClose}>
      <div className="vb-modal" onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: "90vh", overflowY: "auto", maxWidth: 720 }}
        data-testid="cook-modal">
        <div className="flex items-center gap-2 mb-3">
          <Flame size={20} style={{ color: "#dc2626" }} />
          <h2 className="m-0">Cooking session</h2>
        </div>
        <p className="text-xs opacity-70 mb-3">
          Log one or more dishes cooked in the same session. Each dish
          becomes its own record — pick <em>Serve immediately</em> to
          close it out, or <em>Cool &amp; store</em> to follow up with
          cooling / reheat data later.
        </p>
        {err && <div className="vb-pill err mb-3" data-testid="cook-error">{err}</div>}

        <form onSubmit={submit} className="grid gap-3">
          {/* ── Shared header ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Kitchen (facility)"
              hint={facilities.length === 0
                ? "No food-production facility set up for this site — ask your owner to add one under Sites."
                : "Only food-production facilities are shown."}>
              <FacilitySelect facilities={facilities} value={facilityId}
                onChange={setFacilityId} testid="cook-facility" />
            </Field>
            <Field label="Date & time cooked" hint="Applies to every dish in this session.">
              <input className="vb-input" type="datetime-local" required
                value={cookedAt} onChange={(e) => setCookedAt(e.target.value)}
                data-testid="cook-time" />
            </Field>
          </div>
          <Field label="Checked by" hint="Defaults to you; pick another if you're logging for a colleague.">
            <UserSelect users={users} me={me} valueId={checkedBy.id}
              testid="cook-checked-by"
              onPick={({ id, name }) => setCheckedBy({ id, name })} />
          </Field>

          {/* ── Per-item rows ── */}
          <div className="mt-1">
            <div className="text-xs uppercase font-semibold opacity-70 mb-2">
              Dishes cooked
            </div>
            <div className="grid gap-3">
              {items.map((it, i) => {
                const temp = it.temp_c === "" ? null : Number(it.temp_c);
                const rowBreach = temp !== null && !Number.isNaN(temp) && temp < cookMin;
                return (
                  <div key={it._key}
                    className="vb-card p-3 grid gap-2"
                    style={{ background: "rgba(0,0,0,0.02)" }}
                    data-testid={`cook-item-row-${i}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-xs uppercase font-semibold opacity-70">
                        Dish {i + 1}
                      </div>
                      <button type="button" className="vb-btn ghost"
                        onClick={() => removeItem(i)}
                        disabled={items.length === 1}
                        title={items.length === 1 ? "At least one dish is required." : "Remove this dish"}
                        data-testid={`cook-item-remove-${i}`}
                        style={{ padding: "4px 8px" }}>
                        <Trash2 size={14} />
                      </button>
                    </div>

                    <Field label="Type of food">
                      <input className="vb-input" required value={it.food_name}
                        onChange={(e) => setItem(i, { food_name: e.target.value })}
                        data-testid={`cook-item-food-name-${i}`}
                        placeholder="e.g. Chicken curry" />
                    </Field>

                    <Field label={`Core temp (°C) · min ${cookMin}`}>
                      <input className="vb-input" type="number" step="0.1" required
                        value={it.temp_c}
                        onChange={(e) => setItem(i, { temp_c: e.target.value })}
                        data-testid={`cook-item-temp-${i}`} />
                    </Field>

                    <TemperatureOcrField
                      context="cooking"
                      onConfirm={(r) => setItem(i, {
                        temp_c: r.temp_c.toString(),
                        ocr: { photo_path: r.ocr_photo_path,
                               parsed_value: r.ocr_parsed_value,
                               confidence: r.ocr_confidence },
                      })}
                      onFallbackToManual={() => setItem(i, { ocr: null })}
                    />

                    <Field label="After cooking, this dish will be…">
                      <select className="vb-input" value={it.disposition}
                        onChange={(e) => setItem(i, { disposition: e.target.value })}
                        data-testid={`cook-item-disposition-${i}`}>
                        {Object.entries(COOK_DISPOSITIONS).map(([k, v]) =>
                          <option key={k} value={k}>{v}</option>)}
                      </select>
                    </Field>

                    {it.disposition === "cool_store" && (
                      <Field label="Chilling method"
                        hint="Which equipment will this dish be chilled on?">
                        <select className="vb-input" value={it.cool_method}
                          onChange={(e) => setItem(i, { cool_method: e.target.value })}
                          data-testid={`cook-item-cool-method-${i}`}>
                          {Object.entries(COOL_METHODS).map(([k, v]) =>
                            <option key={k} value={k}>{v}</option>)}
                        </select>
                      </Field>
                    )}

                    <Field label="Notes / remarks (optional)">
                      <textarea className="vb-input" rows={2} value={it.notes}
                        onChange={(e) => setItem(i, { notes: e.target.value })}
                        data-testid={`cook-item-notes-${i}`} />
                    </Field>

                    <CorrectiveBox show={rowBreach} value={it.corrective_action}
                      onChange={(e) => setItem(i, { corrective_action: e.target.value })}
                      testid={`cook-item-corrective-${i}`} />
                  </div>
                );
              })}
            </div>
            <button type="button" className="vb-btn ghost text-xs mt-2"
              onClick={addItem} data-testid="cook-add-item">
              <Plus size={13} /> Add another dish
            </button>
          </div>

          {/* ── Footer (flows with content, not sticky — the sticky
              version was blocking the corrective-action textarea on
              short viewports) ── */}
          <div className="flex gap-2 justify-end items-center flex-wrap mt-2">
            <button type="button" className="vb-btn ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="vb-btn" disabled={busy || items.length === 0}
              data-testid="cook-submit">
              {busy
                ? "Saving…"
                : `Log ${items.length} record${items.length > 1 ? "s" : ""} ` +
                  `(${servedCount} serve · ${coolCount} cool-store)`}
            </button>
          </div>
        </form>
      </div>
    </div>
    </Portal>
  );
}

function BatchDetail({ batch, config, canVerify, confirm, onClose, onChanged }) {
  const [stage, setStage] = useState(null);
  const [busy, setBusy] = useState(false);
  const open = batch.status === "open";
  const locked = batch.status === "verified";

  const act = async (fn, okMsg, thenClose) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); thenClose ? onChanged(null) : await onChanged(batch.id); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Action failed"); }
    finally { setBusy(false); }
  };
  const submit = () => act(() => api.put(`/hot-cold-hold/batches/${batch.id}/submit`), "Sent for verification");
  const verify = () => act(() => api.put(`/hot-cold-hold/batches/${batch.id}/verify`, { notes: "" }), "Record verified & locked");
  const disposeBatch = async () => {
    if (!await confirm({
      title: "Dispose this batch?",
      message: "Mark this batch as disposed (food discarded, not consumed). This closes the record and prevents further stages.",
      danger: true, confirmLabel: "Dispose",
    })) return;
    act(() => api.put(`/hot-cold-hold/batches/${batch.id}/dispose`, { reason: "not_used" }), "Batch disposed", true);
  };
  // The batch is only "ready" to submit for supervisor verification once
  // its disposition-specific stages have data. Otherwise a chef could
  // submit a half-finished cook-chill record. Kept explicit so future
  // dispositions (blast-chill etc.) can extend it.
  const isReadyToSubmit = () => {
    if (batch.disposition === "served") return true;
    if (batch.disposition === "cool_store") return isCoolingComplete(batch.cooling) && !!batch.reheat;
    return true;
  };
  const submitMissing = () => {
    if (batch.disposition !== "cool_store") return "";
    if (!batch.cooling) return "Add cooling data before submitting.";
    if (!isCoolingComplete(batch.cooling)) return "Complete the cooling record (end time + end temp) before submitting.";
    if (!batch.reheat) return "Add reheat data before submitting.";
    return "";
  };
  const del = async () => {
    if (!await confirm({ title: "Delete this record?", message: "This removes the cooking record and its stages. Verified records cannot be deleted.", danger: true, confirmLabel: "Delete" })) return;
    act(() => api.delete(`/hot-cold-hold/batches/${batch.id}`), "Record deleted", true);
  };

  return (
    <Portal><div className="vb-modal-bg" onClick={onClose}>
      <div className="vb-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640, maxHeight: "92vh", overflowY: "auto" }} data-testid="batch-detail">
        <div className="flex items-start justify-between mb-1">
          <div>
            <h2 className="m-0 flex items-center gap-2"><Utensils size={18} /> {batch.food_name}</h2>
            <div className="flex items-center gap-2 mt-1">
              <StatusPill status={batch.status} />
              <span className="vb-pill text-[10px]">{COOK_DISPOSITIONS[batch.disposition]}</span>
              {batch.has_breach && <span className="vb-pill err text-[10px]"><AlertTriangle size={11} /> Non-conformance</span>}
            </div>
          </div>
          <button className="vb-btn ghost" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="space-y-3 my-4">
          <StageCard title="Cook" icon={Flame} color="#dc2626" data={batch.cook} rows={[["Temperature", `${batch.cook?.temp_c}°C`], ["When", fmt(batch.cook?.cooked_at)]]} />
          {batch.disposition === "cool_store" && batch.cool_method && (
            <div className="text-[11px] opacity-70 -mt-1 flex items-center gap-1">
              <Snowflake size={11} className="opacity-70" />
              Chilling method: <strong>{COOL_METHODS[batch.cool_method] || batch.cool_method}</strong>
            </div>
          )}
          {batch.cooling && (
            <StageCard
              title="Cool & store"
              icon={Snowflake}
              color="#2563eb"
              data={batch.cooling}
              inProgress={isCoolingInProgress(batch.cooling)}
              rows={isCoolingInProgress(batch.cooling)
                ? [
                    ["Started", `${batch.cooling.start_temp_c}°C · ${fmt(batch.cooling.start_at)}`],
                    ["Status", "Waiting for end time & temp"],
                  ]
                : [
                    ["Start", `${batch.cooling.start_temp_c}°C · ${fmt(batch.cooling.start_at)}`],
                    ["End", `${batch.cooling.end_temp_c}°C · ${fmt(batch.cooling.end_at)}`],
                    ["Duration", `${batch.cooling.duration_hours} hrs`],
                  ]}
            />
          )}
          {batch.reheat && <StageCard title="Reheat" icon={RefreshCw} color="#dc2626" data={batch.reheat} rows={[
            ["Temperature", `${batch.reheat.temp_c}°C`], ["When", fmt(batch.reheat.reheated_at)]]} />}
        </div>

        {open && (
          <div className="flex flex-wrap gap-2 mb-4" data-testid="cook-stage-actions">
            {batch.disposition === "cool_store" && !batch.cooling && (
              <button className="vb-btn ghost sm" onClick={() => setStage("cooling")} data-testid="add-cooling">
                <Plus size={13} /> Add cooling
              </button>
            )}
            {isCoolingInProgress(batch.cooling) && (
              <button className="vb-btn sm" onClick={() => setStage("cooling")} data-testid="complete-cooling">
                <CheckCircle2 size={13} /> Complete cooling
              </button>
            )}
            {isCoolingComplete(batch.cooling) && !batch.reheat && (
              <button className="vb-btn ghost sm" onClick={() => setStage("reheat")} data-testid="add-reheat">
                <Plus size={13} /> Add reheat
              </button>
            )}
            {batch.disposition === "cool_store" && (!batch.cooling || !batch.reheat) && (
              <button className="vb-btn ghost sm" style={{ color: "var(--vb-error)" }}
                onClick={disposeBatch} disabled={busy} data-testid="dispose-batch"
                title="Close this batch without cooling/reheat — food not used">
                <Trash2 size={13} /> Dispose (food not used)
              </button>
            )}
          </div>
        )}

        <div className="flex items-center justify-between gap-2 pt-3 border-t" style={{ borderColor: "var(--vb-border)" }}>
          <div>
            {locked && batch.verification && <span className="text-xs opacity-70 inline-flex items-center gap-1" data-testid="cook-verified-by"><ShieldCheck size={13} style={{ color: "#15803d" }} /> Verified by {batch.verification.verified_by_name} · {fmt(batch.verification.verified_at)}</span>}
          </div>
          <div className="flex gap-2">
            {open && <button className="vb-btn ghost sm" style={{ color: "var(--vb-error)" }} onClick={del} disabled={busy} data-testid="delete-batch"><Trash2 size={13} /> Delete</button>}
            {open && <button className="vb-btn" onClick={submit} disabled={busy || !isReadyToSubmit()}
              title={submitMissing() || undefined}
              data-testid="submit-batch">Submit for verification</button>}
            {batch.status === "awaiting_verification" && canVerify && <button className="vb-btn" onClick={verify} disabled={busy} data-testid="verify-batch"><ShieldCheck size={14} /> Verify &amp; lock</button>}
            {batch.status === "awaiting_verification" && !canVerify && <span className="text-xs opacity-60">Awaiting supervisor/manager verification</span>}
          </div>
        </div>

        {stage && <StageModal type={stage} batch={batch} config={config} onClose={() => setStage(null)} onSaved={async () => { setStage(null); await onChanged(batch.id); }} />}
      </div>
    </div>
    </Portal>
  );
}

function StageCard({ title, icon: Icon, color, data, rows, inProgress = false }) {
  return (
    <div className="p-3 rounded-lg" style={{
      border: "1px solid var(--vb-border)",
      background: data?.breach ? "rgba(220,38,38,0.05)" : (inProgress ? "rgba(37,99,235,0.05)" : "transparent"),
    }} data-testid={`stage-card-${title.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="font-semibold inline-flex items-center gap-2" style={{ color: "var(--vb-text-2)" }}><Icon size={15} style={{ color }} /> {title}</span>
        {inProgress ? (
          <span className="vb-pill text-[10px]" style={{ background: "rgba(37,99,235,0.12)", color: "#1d4ed8" }} data-testid="stage-pill-in-progress">
            <Clock size={10} /> In progress
          </span>
        ) : data?.breach ? (
          <span className="vb-pill err text-[10px]"><AlertTriangle size={10} /> Breach</span>
        ) : (
          <span className="vb-pill ok text-[10px]">In range</span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-sm opacity-80">
        {rows.map(([k, v]) => <div key={k}><span className="opacity-60">{k}: </span>{v}</div>)}
      </div>
      {data?.recorded_by && <div className="text-[11px] opacity-60 mt-1">Checked by {data.recorded_by} · entered by {data.entered_by_name || "—"} · {fmt(data.recorded_at)}</div>}
      {data?.notes && <div className="text-[11px] opacity-70 mt-1">Note: {data.notes}</div>}
      {data?.breach && data?.corrective_action && <div className="text-[11px] mt-1" style={{ color: "#b91c1c" }}>Corrective: {data.corrective_action}</div>}
    </div>
  );
}

function StageModal({ type, batch, config, onClose, onSaved }) {
  const { user: me } = useAuth();
  const users = useSiteUsers();

  // Cooling has a two-step lifecycle:
  //   • start-only    → batch has no cooling record yet
  //   • complete-end  → batch already has an `in_progress` cooling record
  const existingCooling = type === "cooling" ? batch.cooling : null;
  const coolingPhase = type === "cooling"
    ? (isCoolingInProgress(existingCooling) ? "complete" : "start")
    : null;

  const [form, setForm] = useState(() => {
    if (type !== "cooling") {
      return { reheated_at: dtLocalNow(), temp_c: "", recorded_by: me?.name || "", recorded_by_id: me?.id || "", notes: "", corrective_action: "" };
    }
    if (coolingPhase === "complete") {
      // completing an in-progress record — the start fields are read-only,
      // pre-populated from the saved record (rendered in the user's local
      // TZ so the disabled input matches the wall-clock time originally
      // entered). Values never round-trip to the server (backend
      // preserves the original start).
      return {
        start_at: isoToDatetimeLocal(existingCooling.start_at),
        start_temp_c: String(existingCooling.start_temp_c),
        end_at: dtLocalNow(), end_temp_c: "",
        recorded_by: me?.name || "", recorded_by_id: me?.id || "",
        notes: "", corrective_action: "",
      };
    }
    return { start_at: dtLocalNow(), start_temp_c: "", end_at: "", end_temp_c: "",
             recorded_by: me?.name || "", recorded_by_id: me?.id || "",
             notes: "", corrective_action: "" };
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Split cooling breach into WHICH threshold(s) failed so the crew knows
  // exactly what corrective action to record (a temp-only breach and a
  // duration-only breach need very different responses).
  const coolingBreach = (() => {
    if (type !== "cooling" || coolingPhase !== "complete") return { flag: false, reasons: [] };
    if (form.end_temp_c === "" || !form.end_at) return { flag: false, reasons: [] };
    const start = isoToDatetimeLocal(existingCooling?.start_at || form.start_at) || form.start_at;
    // Use the ORIGINAL server-stored start for duration math when we're
    // completing an in-progress record; that way the disabled UTC-shift
    // trick can't distort the duration check.
    const startMs = existingCooling
      ? new Date(existingCooling.start_at).getTime()
      : new Date(start).getTime();
    const hrs = (new Date(form.end_at).getTime() - startMs) / 3600000;
    const maxHrs = config?.cooling_max_hours ?? 4;
    const maxTemp = config?.cooling_end_max_c ?? 4;
    const reasons = [];
    if (hrs > maxHrs) reasons.push(`Cooling took ${hrs.toFixed(2)} hrs (limit ${maxHrs} hrs).`);
    if (Number(form.end_temp_c) > maxTemp) reasons.push(`End temperature ${Number(form.end_temp_c)}°C is above the ${maxTemp}°C limit.`);
    return { flag: reasons.length > 0, reasons };
  })();
  const breach = type === "cooling"
    ? coolingBreach.flag
    : form.temp_c !== "" && Number(form.temp_c) < (config?.reheat_min_c ?? 75);
  const reheatReason = type !== "cooling" && breach
    ? `Reheat temperature ${Number(form.temp_c)}°C is below the ${config?.reheat_min_c ?? 75}°C minimum.`
    : null;

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      if (type === "cooling") {
        const body = {
          start_at: toISO(form.start_at),
          start_temp_c: Number(form.start_temp_c),
          recorded_by: form.recorded_by, recorded_by_id: form.recorded_by_id,
          notes: form.notes, corrective_action: form.corrective_action,
        };
        if (coolingPhase === "complete") {
          body.end_at = toISO(form.end_at);
          body.end_temp_c = Number(form.end_temp_c);
        }
        await api.post(`/hot-cold-hold/batches/${batch.id}/cooling`, body);
      } else {
        await api.post(`/hot-cold-hold/batches/${batch.id}/reheat`, {
          reheated_at: toISO(form.reheated_at), temp_c: Number(form.temp_c),
          recorded_by: form.recorded_by, recorded_by_id: form.recorded_by_id, notes: form.notes, corrective_action: form.corrective_action });
      }
      toast.success(
        type === "cooling"
          ? (coolingPhase === "complete" ? "Cooling completed" : "Cooling started — come back to log end time & temp")
          : "Stage recorded"
      );
      onSaved();
    } catch (e2) { setErr(formatErr(e2.response?.data?.detail) || "Failed to save"); }
    finally { setBusy(false); }
  };

  const modalTitle = type !== "cooling"
    ? "Reheat record"
    : (coolingPhase === "complete" ? "Complete cooling record" : "Start cooling record");
  const submitLabel = type !== "cooling"
    ? (busy ? "Saving…" : "Save record")
    : coolingPhase === "complete"
      ? (busy ? "Saving…" : "Complete cooling")
      : (busy ? "Saving…" : "Save & pause");
  const chillHelp = type === "cooling"
    ? (coolingPhase === "complete"
        ? `Fill in the end time and end temp to close this cooling cycle. Cooling must complete within ${config?.cooling_max_hours ?? 4} hours.`
        : "Log the start time and temperature now — you can come back to enter the end time and temperature once the food has finished cooling.")
    : "";

  return (
    <Portal><div className="vb-modal-bg" onClick={onClose}>
      <div className="vb-modal" onClick={(e) => e.stopPropagation()} style={{ maxHeight: "90vh", overflowY: "auto" }} data-testid={`stage-modal-${type}`}>
        <h2 className="m-0 mb-1">{modalTitle}</h2>
        <p className="text-xs opacity-60 mb-3">Food: <strong>{batch.food_name}</strong>
          {batch.cool_method && type === "cooling" && (
            <> · Method: <strong>{COOL_METHODS[batch.cool_method] || batch.cool_method}</strong></>
          )} · you can backdate the time.
          {type === "reheat" && " Food may only be reheated once — tracking ends after reheat."}
        </p>
        {err && <div className="vb-pill err mb-3" data-testid="stage-error">{err}</div>}
        <form onSubmit={submit} className="grid gap-3">
          {type === "cooling" ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Start time">
                  <input className="vb-input" type="datetime-local" required
                    value={form.start_at} disabled={coolingPhase === "complete"}
                    onChange={(e) => setForm({ ...form, start_at: e.target.value })}
                    data-testid="cooling-start-at" />
                </Field>
                <Field label="Start temp (°C)">
                  <input className="vb-input" type="number" step="0.1" required
                    value={form.start_temp_c} disabled={coolingPhase === "complete"}
                    onChange={(e) => setForm({ ...form, start_temp_c: e.target.value })}
                    data-testid="cooling-start-temp" />
                </Field>
                {coolingPhase === "complete" && (
                  <>
                    <Field label="End time">
                      <input className="vb-input" type="datetime-local" required
                        value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })}
                        data-testid="cooling-end-at" />
                    </Field>
                    <Field label={`End temp (°C) · target ≤ ${config?.cooling_end_max_c ?? 4}`}>
                      <input className="vb-input" type="number" step="0.1" required
                        value={form.end_temp_c} onChange={(e) => setForm({ ...form, end_temp_c: e.target.value })}
                        data-testid="cooling-end-temp" />
                    </Field>
                  </>
                )}
              </div>
              <p className="text-[11px] opacity-60 -mt-1"><Clock size={11} className="inline" /> {chillHelp}</p>
            </>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Time of reheat"><input className="vb-input" type="datetime-local" required value={form.reheated_at} onChange={(e) => setForm({ ...form, reheated_at: e.target.value })} data-testid="reheat-at" /></Field>
              <Field label={`Temp after reheat (°C) · min ${config?.reheat_min_c ?? 75}`}><input className="vb-input" type="number" step="0.1" required value={form.temp_c} onChange={(e) => setForm({ ...form, temp_c: e.target.value })} data-testid="reheat-temp" /></Field>
            </div>
          )}
          <Field label="Recorded by" hint="Defaults to you; pick another if you logged for a colleague.">
            <UserSelect users={users} me={me} valueId={form.recorded_by_id} testid="stage-recorded-by"
              onPick={({ id, name }) => setForm({ ...form, recorded_by_id: id, recorded_by: name })} />
          </Field>
          <Field label="Notes / remarks"><textarea className="vb-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="stage-notes" /></Field>
          <CorrectiveBox show={breach} value={form.corrective_action}
            reasons={type === "cooling" ? coolingBreach.reasons : (reheatReason ? [reheatReason] : [])}
            onChange={(e) => setForm({ ...form, corrective_action: e.target.value })} />
          <div className="flex gap-2 justify-end">
            <button type="button" className="vb-btn ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="vb-btn" disabled={busy} data-testid="stage-submit">{submitLabel}</button>
          </div>
        </form>
      </div>
    </div>
    </Portal>
  );
}

/* ===================== HOT & COLD HOLD TAB ===================== */
function holdSummary(h) {
  const n = (h.items || []).length;
  return n === 1 ? h.items[0].food_name : `${n} dishes`;
}

function HoldingTab({ config, canVerify, needsSite, noFacility, confirm }) {
  const { activeSiteId } = useSite();
  const { tenant } = useAuth();
  const tenantDateFormat = (tenant?.operational?.date_format || "DD/MM/YYYY").toUpperCase();
  const facilities = useKitchenFacilities();
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showNew, setShowNew] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [selected, setSelected] = useState(null);
  const [prepLists, setPrepLists] = useState([]);
  const [openPrepList, setOpenPrepList] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/hot-cold-hold/holdings?status=${statusFilter}`);
    setRows(data.holdings || []);
  }, [statusFilter]);

  const loadPrepLists = useCallback(async () => {
    try {
      const { data } = await api.get("/hot-cold-hold/hold-prep-lists?status=pending");
      const { data: inProg } = await api.get("/hot-cold-hold/hold-prep-lists?status=in_progress");
      const merged = [...(data.prep_lists || []), ...(inProg.prep_lists || [])];
      const map = new Map();
      merged.forEach((p) => map.set(p.id, p));
      setPrepLists([...map.values()].sort((a, b) => a.hold_date.localeCompare(b.hold_date)));
    } catch { /* silent */ }
  }, []);

  useEffect(() => { load(); loadPrepLists(); }, [load, loadPrepLists, activeSiteId]);

  const openPrepFill = async (id) => {
    const { data } = await api.get(`/hot-cold-hold/hold-prep-lists/${id}`);
    setOpenPrepList(data.prep_list);
  };
  const deletePrepList = async (p, hasFills) => {
    if (hasFills) {
      toast.error("Some dishes are already filled — open the list and use Skip on the rest.");
      return;
    }
    if (!(await confirm({
      title: "Delete this prep list?",
      message: `Removes "${p.facility_name} · ${p.hold_date}" (${p.progress?.total || 0} dishes).`,
      confirmLabel: "Delete", danger: true,
    }))) return;
    try {
      await api.delete(`/hot-cold-hold/hold-prep-lists/${p.id}`);
      toast.success("Prep list deleted.");
      await loadPrepLists();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Couldn't delete prep list.");
    }
  };

  const del = async (h) => {
    if (!await confirm({ title: "Delete this holding record?", message: "Verified records cannot be deleted.", danger: true, confirmLabel: "Delete" })) return;
    try { await api.delete(`/hot-cold-hold/holdings/${h.id}`); toast.success("Deleted"); setSelected(null); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Delete failed"); }
  };
  const verify = async (h) => {
    try { await api.put(`/hot-cold-hold/holdings/${h.id}/verify`, { notes: "" }); toast.success("Verified & locked"); setSelected(null); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Verify failed"); }
  };

  return (
    <div data-testid="holding-tab">
      <div className="flex justify-end gap-2 mb-3">
        <button className="vb-btn ghost" onClick={() => setShowUpload(true)} disabled={needsSite || noFacility}
          data-testid="upload-hold-prep-list" title={noFacility ? "Add a food-production facility first." : undefined}>
          <Utensils size={16} /> Upload prep list
        </button>
        <button className="vb-btn" onClick={() => setShowNew(true)} disabled={needsSite || noFacility}
          data-testid="new-holding" title={noFacility ? "Add a food-production facility to this site before logging a record." : undefined}>
          <Plus size={16} /> New hold record
        </button>
      </div>

      {prepLists.length > 0 && (
        <div className="mb-3" data-testid="hold-prep-lists-panel">
          <div className="text-[11px] uppercase tracking-wider opacity-70 mb-2 font-semibold">Today's Hold Prep</div>
          {(() => {
            // Group by hold_date so today's list never sits beside a
            // list scheduled two days out. Uses tenant date format.
            const groups = {};
            prepLists.forEach((p) => {
              const k = p.hold_date;
              (groups[k] = groups[k] || []).push(p);
            });
            const orderedDates = Object.keys(groups).sort();
            const today = new Date().toISOString().slice(0, 10);
            const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
            return orderedDates.map((d) => {
              const label = d === today ? "Today" : d === tomorrow ? "Tomorrow" : "";
              return (
                <div key={d} className="mb-3" data-testid={`hold-prep-list-group-${d}`}>
                  <div className="text-[12px] font-semibold mb-1 flex items-center gap-2">
                    <span>{formatTenantDate(d, tenantDateFormat)}</span>
                    {label && <span className="text-[10px] uppercase tracking-wider opacity-60">· {label}</span>}
                  </div>
                  <div className="flex flex-col gap-2">
                    {groups[d].map((p) => {
                      const pct = p.progress?.total ? Math.round(100 * ((p.progress.filled || 0) + (p.progress.skipped || 0)) / p.progress.total) : 0;
                      const hasFills = (p.progress?.filled || 0) > 0;
                      const derived = p.derived_status || "pending";
                      // Same pill vocabulary as the cooking prep card so
                      // operators see one consistent progression across
                      // the kitchen record surfaces.
                      const pillLabel = {
                        pending: "Pending",
                        in_progress: "In progress",
                        awaiting_verification: "Awaiting verification",
                        verified: "Verified",
                      }[derived] || derived;
                      const pillCls = { verified: "ok", awaiting_verification: "warn" }[derived] || "";
                      return (
                        <div key={p.id} role="button" tabIndex={0}
                          className="vb-card p-3 text-left w-full"
                          style={{ cursor: "pointer" }}
                          onClick={() => openPrepFill(p.id)}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openPrepFill(p.id); } }}
                          data-testid={`hold-prep-list-card-${p.id}`}>
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="text-[13px] font-semibold truncate flex items-center gap-2">
                                {p.mode === "hot"
                                  ? <Flame size={13} style={{ color: "#dc2626" }} />
                                  : <Snowflake size={13} style={{ color: "#2563eb" }} />}
                                {p.facility_name}
                                <span className={`vb-pill ${pillCls} text-[10px]`} data-testid={`hold-prep-list-status-${p.id}`}>{pillLabel}</span>
                              </div>
                              <div className="text-[11px] opacity-70 truncate">
                                {p.mode === "hot" ? "Hot hold" : "Cold hold"}
                                {p.hold_time ? ` · ${p.hold_time}` : ""}
                                {" · "}{(p.progress?.filled || 0)}/{p.progress?.total || 0} filled
                              </div>
                            </div>
                            <button type="button"
                              className="vb-btn ghost sm"
                              onClick={(e) => { e.stopPropagation(); deletePrepList(p, hasFills); }}
                              title={hasFills ? "Some dishes are already filled — skip the rest from inside the list instead." : "Delete this prep list"}
                              data-testid={`hold-prep-list-delete-${p.id}`}
                              style={{ opacity: hasFills ? 0.4 : 1 }}>
                              <Trash2 size={14} />
                            </button>
                            <ChevronRight size={16} className="opacity-40 shrink-0" />
                          </div>
                          <div className="mt-2" style={{ background: "var(--vb-border)", borderRadius: 999, height: 6, overflow: "hidden" }}>
                            <div style={{ background: "var(--vb-success,#166534)", height: "100%", width: `${pct}%`, transition: "width .2s" }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}
      <Filters value={statusFilter} onChange={setStatusFilter} prefix="hold" />
      <div className="vb-card overflow-x-auto">
        <table className="vb-table">
          <thead><tr><th>Food</th><th>Type</th><th>Started</th><th>Serve/discard by</th><th>Status</th><th>Flag</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} style={{ textAlign: "center", padding: 28 }} className="opacity-60">No hold records yet.</td></tr>}
            {rows.map((h) => (
              <tr key={h.id} data-testid={`hold-row-${h.id}`} style={{ cursor: "pointer" }} onClick={() => setSelected(h)}>
                <td><strong>{holdSummary(h)}</strong></td>
                <td>{h.mode === "hot"
                  ? <span className="inline-flex items-center gap-1" style={{ color: "#dc2626" }}><Flame size={13} /> Hot</span>
                  : <span className="inline-flex items-center gap-1" style={{ color: "#2563eb" }}><Snowflake size={13} /> Cold</span>}</td>
                <td className="opacity-70">{fmt(h.start_at)}</td>
                <td className="opacity-70">{fmt(h.max_hold_until)}</td>
                <td><StatusPill status={h.status} /></td>
                <td><FlagPill breach={h.has_breach} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showNew && <HoldingModal config={config} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {showUpload && (
        <HoldPrepListUploadModal
          facilities={facilities}
          onClose={() => setShowUpload(false)}
          onCreated={(p) => { setShowUpload(false); loadPrepLists(); setOpenPrepList(p); }}
        />
      )}
      {openPrepList && (
        <HoldPrepListFillModal
          prepList={openPrepList}
          config={config}
          onClose={() => { setOpenPrepList(null); loadPrepLists(); load(); }}
          onChanged={setOpenPrepList}
        />
      )}
      {selected && (
        <HoldingDetail holding={selected} canVerify={canVerify}
          onClose={() => setSelected(null)} onVerify={() => verify(selected)} onDelete={() => del(selected)} />
      )}
    </div>
  );
}

function HoldingModal({ config, onClose, onSaved }) {
  const { user: me } = useAuth();
  const users = useSiteUsers();
  const facilities = useKitchenFacilities();
  const [mode, setMode] = useState("hot");
  const [facilityId, setFacilityId] = useState("");
  const [recBy, setRecBy] = useState({ id: me?.id || "", name: me?.name || "" });
  const [startAt, setStartAt] = useState(dtLocalNow());
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([{ food_name: "", temp_c: "", corrective_action: "" }]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!facilityId && facilities.length === 1) setFacilityId(facilities[0].id);
  }, [facilities, facilityId]);

  const lineBreach = (l) => l.temp_c !== "" && (mode === "hot"
    ? Number(l.temp_c) < (config?.hot_hold_min_c ?? 63)
    : Number(l.temp_c) > (config?.cold_hold_max_c ?? 4));
  const tempHint = mode === "hot" ? `min ${config?.hot_hold_min_c ?? 63}°C` : `max ${config?.cold_hold_max_c ?? 4}°C`;

  const setLine = (i, patch) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((ls) => [...ls, { food_name: "", temp_c: "", corrective_action: "" }]);
  const removeLine = (i) => setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls));

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await api.post("/hot-cold-hold/holdings", {
        mode, facility_id: facilityId,
        start_at: toISO(startAt), recorded_by: recBy.name, recorded_by_id: recBy.id, notes,
        items: lines.map((l) => ({ food_name: l.food_name, temp_c: Number(l.temp_c), corrective_action: l.corrective_action })),
      });
      toast.success(`Logged ${lines.length} hold ${lines.length > 1 ? "records" : "record"}`); onSaved();
    } catch (e2) { setErr(formatErr(e2.response?.data?.detail) || "Failed to save"); }
    finally { setBusy(false); }
  };

  return (
    <Portal><div className="vb-modal-bg" onClick={onClose}>
      <div className="vb-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640, maxHeight: "92vh", overflowY: "auto" }} data-testid="holding-modal">
        <div className="flex items-center gap-2 mb-3"><Thermometer size={20} style={{ color: "var(--vb-accent)" }} /><h2 className="m-0">Hot / Cold hold record</h2></div>
        {err && <div className="vb-pill err mb-3" data-testid="holding-error">{err}</div>}
        <form onSubmit={submit} className="grid gap-3">
          <Field label="Kitchen (facility)" hint={facilities.length === 0 ? "No food-production facility set up for this site — ask your owner to add one under Sites." : "Only food-production facilities are shown."}>
            <FacilitySelect facilities={facilities} value={facilityId}
              onChange={setFacilityId} testid="holding-facility" />
          </Field>
          <Field label="Holding type">
            <div className="grid grid-cols-2 gap-2" data-testid="holding-mode">
              {[["hot", "Hot holding", Flame, "#dc2626"], ["cold", "Cold holding", Snowflake, "#2563eb"]].map(([val, label, Icon, color]) => (
                <button type="button" key={val} onClick={() => setMode(val)} data-testid={`holding-mode-${val}`}
                  className="text-left p-3 rounded-lg border inline-flex items-center gap-2"
                  style={{ borderColor: mode === val ? "var(--vb-text-2)" : "var(--vb-border)", background: mode === val ? "rgba(120,61,25,0.06)" : "transparent" }}>
                  <Icon size={16} style={{ color }} /> <span className="text-sm font-semibold">{label}</span>
                </button>
              ))}
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Recorded by" hint="Defaults to you.">
              <UserSelect users={users} me={me} valueId={recBy.id} testid="holding-recorded-by"
                onPick={({ id, name }) => setRecBy({ id, name })} />
            </Field>
            <Field label="Start time" hint="You can backdate."><input className="vb-input" type="datetime-local" required value={startAt} onChange={(e) => setStartAt(e.target.value)} data-testid="holding-start-at" /></Field>
          </div>
          <p className="text-[11px] opacity-60 -mt-1"><Clock size={11} className="inline" /> Max hold: {mode === "hot" ? (config?.hot_hold_max_hours ?? 4) : (config?.cold_hold_max_hours ?? 2)} hours · holding temp {tempHint}.</p>

          <div>
            <label className="text-xs uppercase font-semibold opacity-70">Dishes</label>
            <div className="grid gap-2 mt-1" data-testid="holding-lines">
              {lines.map((l, i) => {
                const b = lineBreach(l);
                return (
                  <div key={i} className="grid gap-2 p-2 rounded-lg" style={{ border: "1px solid var(--vb-border)", background: b ? "rgba(220,38,38,0.05)" : "transparent" }} data-testid={`holding-line-${i}`}>
                    <div className="flex gap-2 items-center">
                      <input className="vb-input flex-1" placeholder="Type of food" required value={l.food_name} onChange={(e) => setLine(i, { food_name: e.target.value })} data-testid={`holding-line-food-${i}`} />
                      <input className="vb-input" style={{ width: 120 }} type="number" step="0.1" placeholder="Temp °C" required value={l.temp_c} onChange={(e) => setLine(i, { temp_c: e.target.value })} data-testid={`holding-line-temp-${i}`} />
                      <button type="button" className="vb-btn ghost sm" onClick={() => removeLine(i)} disabled={lines.length === 1} title="Remove line" data-testid={`holding-line-remove-${i}`}><X size={14} /></button>
                    </div>
                    {b && (
                      <input className="vb-input" placeholder="Corrective action (required — out of range)" value={l.corrective_action} onChange={(e) => setLine(i, { corrective_action: e.target.value })} data-testid={`holding-line-corrective-${i}`} />
                    )}
                  </div>
                );
              })}
            </div>
            <button type="button" className="vb-btn ghost sm mt-2" onClick={addLine} data-testid="holding-add-line"><Plus size={13} /> Add dish</button>
          </div>

          <Field label="Notes / remarks (optional)"><textarea className="vb-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="holding-notes" /></Field>
          <div className="flex gap-2 justify-end">
            <button type="button" className="vb-btn ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="vb-btn" disabled={busy} data-testid="holding-submit">{busy ? "Saving…" : "Submit group"}</button>
          </div>
        </form>
      </div>
    </div>
    </Portal>
  );
}

function HoldingDetail({ holding: h, canVerify, onClose, onVerify, onDelete }) {
  const open = h.status !== "verified";
  return (
    <Portal><div className="vb-modal-bg" onClick={onClose}>
      <div className="vb-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560, maxHeight: "92vh", overflowY: "auto" }} data-testid="holding-detail">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="m-0 flex items-center gap-2">
              {h.mode === "hot" ? <Flame size={18} style={{ color: "#dc2626" }} /> : <Snowflake size={18} style={{ color: "#2563eb" }} />}
              {h.mode === "hot" ? "Hot holding" : "Cold holding"}
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <StatusPill status={h.status} />
              <FlagPill breach={h.has_breach} />
            </div>
          </div>
          <button className="vb-btn ghost" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm opacity-85 mb-3">
          <div><span className="opacity-60">Started: </span>{fmt(h.start_at)}</div>
          <div><span className="opacity-60">Serve/discard by: </span>{fmt(h.max_hold_until)}</div>
          <div><span className="opacity-60">Checked by: </span>{h.recorded_by}</div>
          <div><span className="opacity-60">Entered by: </span>{h.entered_by_name || "—"}</div>
        </div>

        <div className="space-y-2 mb-3" data-testid="holding-detail-items">
          {(h.items || []).map((it, i) => (
            <div key={i} className="p-2 rounded-lg" style={{ border: "1px solid var(--vb-border)", background: it.breach ? "rgba(220,38,38,0.05)" : "transparent" }} data-testid={`holding-detail-item-${i}`}>
              <div className="flex items-center justify-between text-sm">
                <span className="font-semibold" style={{ color: "var(--vb-text-2)" }}>{it.food_name}</span>
                <span className="inline-flex items-center gap-2">{it.temp_c}°C {it.breach ? <span className="vb-pill err text-[10px]"><AlertTriangle size={10} /> Breach</span> : <span className="vb-pill ok text-[10px]">In range</span>}</span>
              </div>
              {it.breach && it.corrective_action && <div className="text-[11px] mt-1" style={{ color: "#b91c1c" }}>Corrective: {it.corrective_action}</div>}
            </div>
          ))}
        </div>
        {h.notes && <div className="text-[12px] opacity-70 mb-2">Note: {h.notes}</div>}

        <div className="flex items-center justify-between gap-2 pt-3 border-t" style={{ borderColor: "var(--vb-border)" }}>
          <div>
            {h.status === "verified" && h.verification && <span className="text-xs opacity-70 inline-flex items-center gap-1" data-testid="hold-verified-by"><ShieldCheck size={13} style={{ color: "#15803d" }} /> Verified by {h.verification.verified_by_name} · {fmt(h.verification.verified_at)}</span>}
          </div>
          <div className="flex gap-2">
            {open && <button className="vb-btn ghost sm" style={{ color: "var(--vb-error)" }} onClick={onDelete} data-testid="delete-holding"><Trash2 size={13} /> Delete</button>}
            {open && canVerify && <button className="vb-btn" onClick={onVerify} data-testid="verify-holding"><ShieldCheck size={14} /> Verify &amp; lock</button>}
            {open && !canVerify && <span className="text-xs opacity-60">Awaiting supervisor/manager verification</span>}
          </div>
        </div>
      </div>
    </div>
    </Portal>
  );
}
